/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDebouncedSearchBox } from 'src/core/components/LuisDebouncedSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { SearchResultsAnnouncer } from 'src/core/components/SearchResultsAnnouncer';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { TestBladeHeader } from 'src/luis/projects/testPane/components/common/TestBladeHeader';
import { SchemaType } from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';

export type LabelsFilterItem = {
    labelName: string;
    isChecked: boolean;
};

export type ModelPerformanceFiltersBladeProps = {
    isOpen: boolean;
    onDismiss: () => void;
    pivotName?: SchemaType;
    isExtractionProject: boolean;
    labelsToFilter: LabelsFilterItem[];
    onFiltersChange: (newSelectedLabels: LabelsFilterItem[]) => void;
};

const messages = defineMessages({
    header: {
        id: 'modelGuidance.modelPerformanceFiltersBlade.header',
        defaultMessage: 'Filters'
    },
    search: {
        id: 'modelGuidance.modelPerformanceFiltersBlade.search',
        defaultMessage: 'Search'
    },
    noLabelsAvailable: {
        id: 'modelGuidance.modelPerformanceFiltersBlade.noLabelsAvailable',
        defaultMessage: 'No {labelType} available.'
    },
    noLabelsFound: {
        id: 'modelGuidance.modelPerformanceFiltersBlade.noLabelsFound',
        defaultMessage: 'No {labelType} found.'
    },
    reset: {
        id: 'modelGuidance.modelPerformanceFiltersBlade.reset',
        defaultMessage: 'Reset all filters'
    },
    apply: {
        id: 'modelGuidance.modelPerformanceFiltersBlade.apply',
        defaultMessage: 'Apply'
    },
    cancel: {
        id: 'modelGuidance.modelPerformanceFiltersBlade.cancel',
        defaultMessage: 'Cancel'
    },
    title: {
        id: 'modelGuidance.modelPerformanceFiltersBlade.title',
        defaultMessage: '{labelType} type'
    }
});

const headerTheme = getScopedTheme('GlobalHeaderBlades');
const itemsLeftMargin = '6px';

export const ModelPerformanceFiltersBlade = (props: ModelPerformanceFiltersBladeProps) => {
    const { isOpen, onDismiss, isExtractionProject, pivotName, labelsToFilter, onFiltersChange } = props;

    const { localize } = useLocalization();
    const [filterItems, updateFilterItems] = React.useState(labelsToFilter);
    const [lastFocusedElement, setLastFocusedElement] = React.useState(null);

    React.useEffect(() => {
        document.getElementById(lastFocusedElement)?.focus();
    }, [lastFocusedElement]);

    React.useEffect(() => {
        updateFilterItems(labelsToFilter);
    }, [labelsToFilter]);

    const getModelName = () => {
        switch (pivotName) {
            case 'Entities':
                return 'entities';
            case 'Intents':
                return 'intents';
            default:
        }
    };

    const getCapitalizedModelName = () => {
        switch (pivotName) {
            case 'Entities':
                return 'Entity';
            case 'Intents':
                return 'Intent';
            default:
        }
    };

    const ItemsSection = ({
        items,
        title,
        isSearchHidden = false,
        toggleFilter
    }: {
        items: LabelsFilterItem[];
        title: string;
        isSearchHidden?: boolean;
        toggleFilter: (item: LabelsFilterItem) => void;
    }) => {
        const [query, setQuery] = React.useState('');

        const filteredItems = substringSearchStrategy(items, query, ['labelName']);

        return items.length ? (
            <Stack tokens={{ childrenGap: 12, padding: '0 0 0 12px' }}>
                <LuisText variant="small" fontWeight={600} styles={{ root: { marginLeft: itemsLeftMargin } }}>
                    {title}
                </LuisText>
                {!isSearchHidden && (
                    <LuisDebouncedSearchBox
                        value={query}
                        onChange={(_, v) => setQuery(v)}
                        placeholder={localize(messages.search)}
                        debounceTime={0}
                    />
                )}

                <SearchResultsAnnouncer isActive={query.length > 0} itemsLength={filteredItems.length} />

                {filteredItems.length ? (
                    <Stack key={title} tokens={{ childrenGap: 6 }} styles={{ root: { padding: '5px 0' } }}>
                        {filteredItems.map(item => (
                            <Checkbox
                                key={item.labelName}
                                id={`${item.labelName}-${item.isChecked}`}
                                styles={{ root: { marginLeft: itemsLeftMargin } }}
                                checked={item.isChecked}
                                onChange={() => {
                                    toggleFilter(item);
                                    setLastFocusedElement(`${item.labelName}-${!item.isChecked}`);
                                }}
                                label={item.labelName}
                                onRenderLabel={() => (
                                    <TruncatedText title={item.labelName} maxWidth="300px">
                                        {item.labelName}
                                    </TruncatedText>
                                )}
                                ariaLabel={item.labelName}
                            />
                        ))}
                    </Stack>
                ) : (
                    <LuisText variant="small" styles={{ root: { margin: 'auto' } }}>
                        {localize(messages.noLabelsFound, {
                            labelType: pivotName ? getModelName() : isExtractionProject ? 'entities' : 'classes'
                        })}
                    </LuisText>
                )}
            </Stack>
        ) : (
            <LuisText variant="small" styles={{ root: { margin: 'auto' } }}>
                {localize(messages.noLabelsAvailable, {
                    labelType: pivotName ? getModelName() : isExtractionProject ? 'entities' : 'classes'
                })}
            </LuisText>
        );
    };

    const handleToggleFilterItem = (item: LabelsFilterItem) => {
        updateFilterItems(filterItems.map(i => (i.labelName === item.labelName ? { ...i, isChecked: !i.isChecked } : i)));
    };

    const resetAllFilters = () => {
        updateFilterItems(filterItems.map(i => ({ ...i, isChecked: false })));
    };

    const BladeContent = (
        <Stack styles={{ root: { height: '100%' } }} verticalFill>
            <Stack styles={{ root: { overflow: 'auto', height: '95%' } }} tokens={{ childrenGap: 16, padding: '12px 0 0 0' }}>
                <ItemsSection
                    items={filterItems}
                    toggleFilter={handleToggleFilterItem}
                    title={localize(messages.title, {
                        labelType: pivotName ? getCapitalizedModelName() : isExtractionProject ? 'Entity' : 'Class'
                    })}
                />
            </Stack>
            <Stack horizontal horizontalAlign="space-between">
                <Stack horizontal tokens={{ childrenGap: 6 }}>
                    <PrimaryButton
                        onClick={() => {
                            onFiltersChange(filterItems);
                            onDismiss();
                        }}>
                        {localize(messages.apply)}
                    </PrimaryButton>
                    <DefaultButton onClick={onDismiss}>{localize(messages.cancel)}</DefaultButton>
                </Stack>
                <Link onClick={resetAllFilters}>{localize(messages.reset)}</Link>
            </Stack>
        </Stack>
    );

    const blades = [
        {
            key: 'filtersBladeId',
            header: <TestBladeHeader>{localize(messages.header)}</TestBladeHeader>,
            content: BladeContent
        }
    ];

    return (
        <StackBladeHost
            isOpen={isOpen}
            blades={blades}
            onDismiss={onDismiss}
            onLastBladeDismissed={onDismiss}
            topSpacing={headerTheme.topSpacing}
        />
    );
};
